import EventEmitter from 'eventemitter3';
import styles from './styles.module.scss';

type Option = {
  label: string;
  value: string;
};

export type SelectConfig = {
  options: Option[];
  theme?: {
    [key: string]: string;
  };
};

type EventTypes = {
  'change:select': (dataItem: string[]) => void;
};

export default class Select extends EventEmitter<EventTypes> {
  private __$dom!: HTMLElement;

  private __$selectBox!: HTMLElement;

  private __$selectedOptions!: HTMLElement;

  private __$optionsList!: HTMLElement;

  private __$placeholder!: HTMLElement;

  private __$selectContainer!: HTMLElement;

  private __$dropdown!: HTMLElement;

  private __themeConfig?: SelectConfig['theme'];

  /**  选择框外点击事件处理 */
  private __resetClickHandler: (event: Event) => void;

  /** 配置数据 */
  private __options: Option[];

  /** 选中项的维护数组 */
  private __selectedValues: string[] = [];

  private __selectedLabels: string[] = [];

  constructor($container: HTMLElement, selectConfig: SelectConfig) {
    super();

    this.__options = selectConfig.options;
    this.__themeConfig = selectConfig.theme;
    this.__$dom = document.createElement('div');
    this.__$dom.classList.add(styles.container, styles['theme-container']);
    this.__resetClickHandler = (event: Event) => {
      const target = event.target as HTMLElement;
      if (!this.__$selectContainer.contains(target)) {
        this.__$selectContainer.classList.remove(styles['select-container-select']);
        this.__$optionsList.classList.add(styles.hidden);
      }
    };

    $container.appendChild(this.__$dom);

    this.__buildDom();
    this.__initTheme();
    this.__renderSelectedOptions();
    this.__renderOptionsList();
    this.__bindEvent();
  }

  /** 初始化主题 */
  private __initTheme() {
    const themeConfig = this.__themeConfig;
    if (!themeConfig) {
      return;
    }

    Object.keys(themeConfig).forEach(key => {
      this.__$dom.style.setProperty(key, themeConfig[key]);
    });
  }

  /** 选择框外点击时，初始化表格的状态 */
  private __resetClickEvent() {
    document.addEventListener('click', this.__resetClickHandler);
  }

  /** 移除选择框外的点击事件 */
  private __removeResetClickEvent() {
    document.removeEventListener('click', this.__resetClickHandler);
  }

  private __buildDom() {
    this.__$selectBox = document.createElement('div');
    this.__$selectBox.classList.add(styles['select-box']);
    this.__$selectContainer = document.createElement('div');
    this.__$selectContainer.classList.add(styles['select-container']);
    this.__$selectedOptions = document.createElement('div');
    this.__$selectedOptions.classList.add(styles['select-options']);
    this.__$dropdown = document.createElement('div');
    this.__$dropdown.classList.add(styles.dropdown);
    const $dropdownImg = document.createElement('div');
    $dropdownImg.classList.add(styles['dropdown-img']);
    this.__$optionsList = document.createElement('div');
    this.__$optionsList.classList.add(styles['options-list'], styles.hidden);
    this.__$placeholder = document.createElement('span');
    this.__$placeholder.classList.add(styles['place-holder']);
    this.__$placeholder.innerHTML = '状态筛选';

    this.__$dropdown.appendChild($dropdownImg);
    this.__$selectBox.appendChild(this.__$selectedOptions);
    this.__$selectContainer.appendChild(this.__$optionsList);
    this.__$selectContainer.appendChild(this.__$selectBox);
    this.__$selectContainer.appendChild(this.__$dropdown);
    this.__$dom.appendChild(this.__$selectContainer);
  }

  /** 渲染选择框内选中项 */
  private __renderSelectedOptions() {
    const options = this.__options;
    const selectedValues = this.__selectedValues;
    this.__$selectedOptions.innerHTML = '';
    let maxOptionElement: HTMLElement;
    if (selectedValues.length === 0) {
      this.__$selectedOptions.appendChild(this.__$placeholder);
    } else {
      selectedValues.forEach((value, index) => {
        const option = options.find(opt => opt.value === value);
        if (option && index < 2) {
          const optionElement = document.createElement('span');
          optionElement.textContent = option.label;
          optionElement.classList.add(styles.option);
          this.__$selectedOptions.appendChild(optionElement);
        }
        if (index === 2) {
          maxOptionElement = document.createElement('span');
          maxOptionElement.classList.add(styles.option);
          maxOptionElement.textContent = '+1';
          this.__$selectedOptions.appendChild(maxOptionElement);
        }
        if (index > 2) {
          maxOptionElement.textContent = `+${index - (2 - 1)}`;
        }
      });
    }
  }

  /** 渲染下拉项 */
  private __renderOptionsList() {
    const options = this.__options;
    const selectedValues = this.__selectedValues;
    this.__$optionsList.innerHTML = '';
    options.forEach(option => {
      const optionElement = document.createElement('div');
      optionElement.textContent = option.label;
      optionElement.classList.add(styles.label);
      if (selectedValues.includes(option.value)) {
        optionElement.classList.remove(styles.label);
        optionElement.classList.add(styles['label-select']);
      }
      optionElement.addEventListener('click', () => this.__toggleOption(option));
      this.__$optionsList.appendChild(optionElement);
    });
  }

  /** 更新选中项的维护数组 */
  private __toggleOption(option: Option) {
    const selectedValues = this.__selectedValues;
    const selectedLabels = this.__selectedLabels;
    if (selectedValues.includes(option.value)) {
      this.__selectedValues = selectedValues.filter(v => v !== option.value);
      this.__selectedLabels = selectedLabels.filter(k => k !== option.label);
    } else {
      this.__selectedValues.push(option.value);
      this.__selectedLabels.push(option.label);
    }
    this.emit('change:select', this.__selectedLabels);
    this.__renderSelectedOptions();
    this.__renderOptionsList();
  }

  /** 隐藏下拉列表 */
  private __toggleOptionsList() {
    this.__$optionsList.classList.toggle(styles.hidden);
  }

  private __bindEvent() {
    this.__$selectContainer.addEventListener('click', () => {
      this.__toggleOptionsList();
      this.__$selectContainer.classList.add(styles['select-container-select']);
    });

    this.__$optionsList.addEventListener('click', event => {
      event.stopPropagation();
    });

    this.__resetClickEvent();
  }

  destroy() {
    this.__removeResetClickEvent();
    this.__$dom.remove();
    this.__$dom.innerHTML = '';
  }
}
